import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Formik } from 'formik';
import axios from 'axios';
import {
  AppCard,
  AppInput,
  AppNoInfo,
  AppNotificationAlert,
} from "@/layout/AppComponents";
import ReactDOMServer from "react-dom/server";
import styled from "styled-components";
import { axiosPath } from "../../../../routing/helpers/router";
import Translator from "../../../../layout/components/helpers/Translator";
import BreadcrumbHandler from "../../../../layout/components/breadcrumb/BreadcrumbHandler";
import AppSubmitButton from "../../../../layout/components/form/AppSubmitButton";
import errorHandler from "@/layout/components/error/ErrorHandler";
import * as currentPageAction from "../../../../store/actions/currentPageAction";
import ModuleComsaEnvironmentalSideBarReports from "../ModuleComsaEnvironmentalSideBarReports";
import useIsMounted from "../../../../layout/components/hooks/useIsMounted";

export default function ModuleComsaEnvironmentalOfficeNotesAndObservationsScreen() {
  const { moduleBridgeHex, subModuleBridgeHex, plantHex } = useParams();
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const moduleData = useSelector((state) => state.currentPage?.moduleData);
  const [initialValues, setInitialValues] = useState();

  const loadData = useCallback(
    async () =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_get"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            sheet: "notesAndObservations",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            setInitialValues(
              r.data?.sheetData ?? {
                manufactured: "",
                reviewAndApproved: "",
                pcmaTechnician: "",
                centerResponsible: "",
              }
            );
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  useEffect(() => {
    loadData();
  }, []);

  // > LOAD MODULE DATA //
  const loadModuleData = useCallback(async () => {
    axios
      .post(axiosPath("api_r_user_module_comsaEnvironmental_getModuleData"), {
        moduleBridgeHex,
        subModuleBridgeHex,
        plantHex,
      })
      .then((r) => {
        if (isMounted.current) {
          dispatch(currentPageAction.change("moduleData", r.data));
        }
      });
  }, []);

  useEffect(() => {
    loadModuleData();
  }, []);
  // > LOAD MODULE DATA //

  const handleSave = useCallback(
    async (payload) =>
      axios
        .post(
          axiosPath("api_r_user_module_comsaEnvironmental_section_sheet_edit"),
          {
            moduleBridgeHex,
            subModuleBridgeHex,
            plantHex,
            payload,
            sheet: "notesAndObservations",
          }
        )
        .then((r) => {
          if (isMounted.current) {
            dispatch(
              currentPageAction.change("moduleData", r.data?.moduleData, true)
            );
            AppNotificationAlert({
              message: ReactDOMServer.renderToString(
                <Translator file="module-comsaEnvironmental" name="completed" />
              ),
              type: "success",
              duration: 3000,
            });
          }
        })
        .catch((e) => dispatch(errorHandler(e))),
    [moduleBridgeHex, subModuleBridgeHex, plantHex]
  );

  if (!moduleData) return <AppNoInfo />;

  return (
    <div className="pl-2">
      <BreadcrumbHandler
        paths={
          moduleData
            ? [
                {
                  name: `${
                    moduleData.plantName
                  } - ${ReactDOMServer.renderToString(
                    <Translator
                      file="module-legalCompliance"
                      name="evaluation"
                    />
                  )}`,
                  route: {
                    name: "user_module_legalCompliance_evaluation",
                    params: {
                      moduleBridgeHex,
                      plantHex,
                      page: 1,
                      filtersHex: 0,
                    },
                  },
                },
                {
                  name: ReactDOMServer.renderToString(
                    <Translator
                      file="module-comsaEnvironmental"
                      name="office"
                    />
                  ),
                },
              ]
            : [{ name: "..." }]
        }
      />
      <section className="content">
        <ModuleComsaEnvironmentalSideBarReports activeRoute="user_module_comsaEnvironmental_office_notes_and_observations" />
        <div className="row">
          <div className="col-12">
            <Title className="mb-4">
              <Translator
                file="module-comsaEnvironmental"
                name="office.environmental.aspects.identification.evaluation"
              />
            </Title>
            <Subtitle>
              <Translator
                file="module-comsaEnvironmental"
                name="office.notes.and.observations"
              />
            </Subtitle>
          </div>
        </div>
        {initialValues && (
          <Formik
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSave}
          >
            {({ setFieldValue, handleSubmit, values, isSubmitting }) => (
              <form onSubmit={handleSubmit}>
                <AppCard>
                  <div className="row">
                    <div className="col-lg-6">
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="prepared"
                          />
                        )}
                        onChange={(value) =>
                          setFieldValue("manufactured", value)
                        }
                        value={values.manufactured}
                        isClearable
                      />
                    </div>
                    <div className="col-lg-6">
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="reviewed.approved"
                          />
                        )}
                        onChange={(value) =>
                          setFieldValue("reviewAndApproved", value)
                        }
                        value={values.reviewAndApproved}
                        isClearable
                      />
                    </div>
                    <div className="col-lg-6">
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="pcma.technician"
                          />
                        )}
                        onChange={(value) =>
                          setFieldValue("pcmaTechnician", value)
                        }
                        value={values.pcmaTechnician}
                        isClearable
                      />
                    </div>
                    <div className="col-lg-6">
                      <AppInput
                        title={ReactDOMServer.renderToString(
                          <Translator
                            file="module-comsaEnvironmental"
                            name="center.responsible"
                          />
                        )}
                        onChange={(value) =>
                          setFieldValue("centerResponsible", value)
                        }
                        value={values.centerResponsible}
                        isClearable
                      />
                    </div>
                  </div>
                </AppCard>
                <SubmitWrapper>
                  <AppSubmitButton
                    className="btn btn-sm btn-success"
                    isSubmitting={isSubmitting}
                  >
                    <Translator file="module-comsaEnvironmental" name="save" />
                  </AppSubmitButton>
                </SubmitWrapper>
              </form>
            )}
          </Formik>
        )}
      </section>
    </div>
  );
}

const Title = styled.h1`
  text-align: center;
  font-size: 30px;
`;

const Subtitle = styled.h4`
  text-align: center;
`;

const SubmitWrapper = styled.div`
  position: fixed;
  right: 15px;
  bottom: 8px;
  z-index: 10;
`;
